/**
 * What this page HEARD of a round, on `performance.now()` (hard close rev 4,
 * DESIGN §2.3, §6).
 *
 * The board's countdown is counted from the moment `race_ready` arrived here,
 * never from a stamp on the row: no device clock is read against the server's.
 * So the arrival is written down the instant the row comes off the socket
 * (`handleRaceEventCreated`), before it enters the game store, and a board
 * mounted later in the same page reads the same moment.
 *
 * A row the page did not hear live (read off the board on join) has no entry:
 * a late joiner gets no countdown and locks on `race_starting`'s arrival alone,
 * and one that joins after `race_starting` sends nothing that round.
 *
 * Two more facts ride on the same entry, both for `round:betting-closed`, which
 * a hard round posts at its LOCK: whether the round's `race_ready` was heard
 * hard, and whether the close has been posted for it already, so the lock and
 * the row that comes after it post it once between them.
 *
 * Only the last few rounds are kept: nothing here grows with a session.
 */

export type HeardRow = 'race_ready' | 'race_starting'

interface Heard {
  race_ready?: number
  race_starting?: number
  hard?: true
  closePosted?: true
}

/** Rounds remembered: the open one and a few behind it, across every game on the page. */
const ROUNDS_KEPT = 32

const heard = new Map<string, Heard>()

function entryOf(roundId: string): Heard {
  const entry = heard.get(roundId) ?? {}
  heard.delete(roundId)
  heard.set(roundId, entry)
  while (heard.size > ROUNDS_KEPT) {
    const oldest = heard.keys().next().value
    if (oldest === undefined) break
    heard.delete(oldest)
  }
  return entry
}

export function isHeardRow(eventType: string): eventType is HeardRow {
  return eventType === 'race_ready' || eventType === 'race_starting'
}

/**
 * Write down the arrival of a round's row. The first arrival stands: a
 * redelivery moves nothing. `hard`: the `race_ready` said `bet_close: "hard"`.
 */
export function noteRaceEventHeard(
  roundId: string,
  eventType: HeardRow,
  at: number,
  hard?: boolean
): void {
  const entry = entryOf(roundId)
  if (entry[eventType] !== undefined) return
  entry[eventType] = at
  if (eventType === 'race_ready' && hard) entry.hard = true
}

/** When this page heard the row live, or undefined (not heard, or read off the board). */
export function raceEventHeardAt(
  roundId: string | undefined,
  eventType: HeardRow
): number | undefined {
  if (roundId === undefined) return undefined
  return heard.get(roundId)?.[eventType]
}

/** The round's `race_ready` was heard live, and hard. */
export function isHardRoundHeard(roundId: string): boolean {
  return heard.get(roundId)?.hard === true
}

/**
 * Claim the posting of `round:betting-closed` for a round: true for the first
 * caller, false for every one after it.
 */
export function claimBettingClosedPost(roundId: string): boolean {
  const entry = entryOf(roundId)
  if (entry.closePosted) return false
  entry.closePosted = true
  return true
}

/** Tests only. */
export function __resetRaceEventHeardForTests(): void {
  heard.clear()
}
